import { Copy, MapPin, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { Address } from './api';
import { addressLine, mapsHref, mapsPlatform, telHref } from './maps';

/**
 * An address as a link to the device's maps app, readable as text, with «Copia» beside it (board
 * 3.1; U16: no map in an object page). Nothing when the address is empty.
 */
export function AddressLink({ address }: { readonly address: Address | null | undefined }) {
  const { t } = useTranslation('suppliers');
  const line = addressLine(address);
  if (line === null || address === null || address === undefined) return null;
  const href = mapsHref(address, mapsPlatform(navigator.userAgent));

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('address.copied'));
    } catch {
      toast.error(t('address.copyFailed'));
    }
  }

  return (
    <span className="inline-flex max-w-full items-center gap-1">
      <a
        href={href ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('address.open', { address: line })}
        className="text-link inline-flex min-h-11 min-w-0 items-center gap-1.5 hover:underline"
      >
        <MapPin aria-hidden="true" className="size-4 shrink-0" />
        <span className="min-w-0 break-words">{line}</span>
      </a>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('address.copy', { address: line })}
        title={t('address.copyShort')}
        onClick={() => void copy(line)}
      >
        <Copy aria-hidden="true" />
      </Button>
    </span>
  );
}

/** A phone number that calls (board 3.1). */
export function PhoneLink({ phone }: { readonly phone: string | null | undefined }) {
  const { t } = useTranslation('suppliers');
  if (!phone) return null;
  return (
    <a
      href={telHref(phone)}
      aria-label={t('phone.call', { phone })}
      className="text-link inline-flex min-h-11 items-center gap-1.5 hover:underline"
    >
      <Phone aria-hidden="true" className="size-4 shrink-0" />
      {phone}
    </a>
  );
}
